import apiClient from './apiClient';

export type AiChatThreadStatus = 'active' | 'archived';
export type AiChatMessageRole = 'user' | 'assistant' | 'tool';
export type AiChatMessageStatus = 'streaming' | 'complete' | 'failed';

export interface AiChatThreadSummary {
  id: string;
  title: string;
  patientId: string | null;
  status: AiChatThreadStatus;
  messageCount: number;
  tokensIn: number;
  tokensOut: number;
  lastMessageAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AiChatMessageView {
  id: string;
  role: AiChatMessageRole;
  content: string;
  tokensIn: number;
  tokensOut: number;
  modelName: string | null;
  status: AiChatMessageStatus;
  toolCall: Record<string, unknown> | null;
  /** Short-lived presigned view URLs for persisted image attachments
   *  (user messages). Absent for text-only messages. */
  imageUrls?: string[];
  createdAt: string;
}

export interface AiChatListResult {
  items: AiChatThreadSummary[];
  nextCursor: string | null;
}

export interface AiChatThreadDetail {
  thread: AiChatThreadSummary & { tenantId: string; userId: string };
  messages: AiChatMessageView[];
}

export type ChatContextItemType =
  | 'patient'
  | 'medical_record'
  | 'draft_record'
  | 'lab_order'
  | 'document'
  | 'artifact';

export interface ChatContextItemView {
  id: string;
  threadId: string;
  itemType: ChatContextItemType;
  refId: string;
  label: string | null;
  pinned: boolean;
  createdAt: string;
}

interface ApiEnvelope<T> {
  success: boolean;
  data: T;
}

const unwrap = <T,>(payload: ApiEnvelope<T> | T): T => {
  if (
    payload &&
    typeof payload === 'object' &&
    'success' in (payload as object) &&
    'data' in (payload as object)
  ) {
    return (payload as ApiEnvelope<T>).data;
  }
  return payload as T;
};

export interface ListAiChatsParams {
  patientId?: string;
  status?: AiChatThreadStatus | 'all';
  cursor?: string;
  limit?: number;
}

export const aiChatsApi = {
  list(params: ListAiChatsParams = {}): Promise<AiChatListResult> {
    return apiClient
      .get('/ai/chats', { params })
      .then((r) => unwrap<AiChatListResult>(r.data));
  },

  get(id: string): Promise<AiChatThreadDetail> {
    return apiClient
      .get(`/ai/chats/${id}`)
      .then((r) => unwrap<AiChatThreadDetail>(r.data));
  },

  create(opts: { patientId?: string; title?: string } = {}): Promise<AiChatThreadSummary> {
    return apiClient
      .post('/ai/chats', opts)
      .then((r) => unwrap<AiChatThreadSummary>(r.data));
  },

  rename(id: string, title: string): Promise<AiChatThreadSummary> {
    return apiClient
      .patch(`/ai/chats/${id}`, { title })
      .then((r) => unwrap<AiChatThreadSummary>(r.data));
  },

  archive(id: string): Promise<AiChatThreadSummary> {
    return apiClient
      .patch(`/ai/chats/${id}`, { status: 'archived' })
      .then((r) => unwrap<AiChatThreadSummary>(r.data));
  },

  unarchive(id: string): Promise<AiChatThreadSummary> {
    return apiClient
      .patch(`/ai/chats/${id}`, { status: 'active' })
      .then((r) => unwrap<AiChatThreadSummary>(r.data));
  },

  remove(id: string): Promise<void> {
    return apiClient.delete(`/ai/chats/${id}`).then(() => undefined);
  },

  /**
   * Rollback: delete every message in the thread created AFTER the given
   * message (the message itself is kept). Used by "Restore to here" to prune
   * a corrupted/gibberish tail so it can't return on reload or another device.
   */
  truncateAfter(id: string, afterMessageId: string): Promise<void> {
    return apiClient
      .post(`/ai/chats/${id}/truncate`, { afterMessageId })
      .then(() => undefined);
  },

  // ---- Context rail (workspace) -------------------------------------------

  listContext(threadId: string): Promise<ChatContextItemView[]> {
    return apiClient
      .get(`/ai/chats/${threadId}/context`)
      .then((r) => unwrap<ChatContextItemView[]>(r.data));
  },

  /** Idempotent: re-attaching the same (type, ref) refreshes label/pinned. */
  attachContext(
    threadId: string,
    item: {
      itemType: ChatContextItemType;
      refId: string;
      label?: string;
      pinned?: boolean;
    },
  ): Promise<ChatContextItemView> {
    return apiClient
      .post(`/ai/chats/${threadId}/context`, item)
      .then((r) => unwrap<ChatContextItemView>(r.data));
  },

  detachContext(threadId: string, itemId: string): Promise<void> {
    return apiClient
      .delete(`/ai/chats/${threadId}/context/${itemId}`)
      .then(() => undefined);
  },
};
